'use client';
import * as React from 'react';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store';
import RepeatRounded from '@mui/icons-material/RepeatRounded';
import { CalendarGrid } from '@mui/x-scheduler-internals/calendar-grid';
import type {
  SchedulerEventSide,
  SchedulerRenderableEventOccurrence,
} from '@mui/x-scheduler-internals/models';
import type { EventCalendarState } from '@mui/x-scheduler-internals/use-event-calendar';
import { schedulerEventSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-internals/use-event-calendar-store-context';
import { eventCalendarViewSelectors } from '@mui/x-scheduler-internals/event-calendar-selectors';
import { getOccurrenceDataTimezone } from '@mui/x-scheduler-internals/internals';
import type { DayGridEventProps } from './DayGridEvent.types';
import { isOccurrenceAllDayOrMultipleDay } from '../../../utils/event-utils';
import { EventDragPreview } from '../../../components/event-drag-preview';
import { useFormatTime } from '../../../hooks/useFormatTime';
import { useEventAccessibleName } from '../../../hooks/useEventAccessibleName';
import type { PaletteName } from '../../../utils/tokens';
import { getPaletteVariants } from '../../../utils/tokens';
import { useEventCalendarStyledContext } from '../../../../event-calendar/EventCalendarStyledContext';
import { eventCalendarClasses } from '../../../../event-calendar/eventCalendarClasses';
import {
  ARROW_DEPTH,
  LEFT_ARROW_CLIP,
  RIGHT_ARROW_CLIP,
  BOTH_ARROWS_CLIP,
  getArrowFocusVisibleStyles,
} from '../arrowClips';

const DayGridEventBaseStyles = (theme: any) => ({
  containerType: 'inline-size',
  borderRadius: theme.shape.borderRadius * 0.75,
  minWidth: 18,
  height: 18,
  cursor: 'pointer',
  position: 'relative',
  zIndex: 1,
  gridRow: 'var(--grid-row)',
  gridColumn: 1,
  padding: `0 ${theme.spacing(0.5)}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: `calc(var(--grid-column-span) * 100% + (var(--grid-column-span) - 1) * (2 * ${theme.spacing(0.5)} + 1px))`,
  '&[data-dragging], &[data-resizing]': {
    opacity: 0.5,
  },
  variants: getPaletteVariants(theme),
});

const DayGridEventRoot = styled(CalendarGrid.DayEvent, {
  name: 'MuiEventCalendar',
  slot: 'DayGridEvent',
})<{ 'data-variant'?: 'filled' | 'invisible' | 'compact' | 'placeholder'; palette?: PaletteName }>(
  ({ theme }) => ({
    ...(DayGridEventBaseStyles(theme) as any),
    '&:focus-visible': {
      outline: '2px solid var(--event-surface-accent)',
      outlineOffset: 1,
    },
    '&[data-variant="filled"]': {
      backgroundColor: 'var(--event-surface-bold)',
      '&:active': {},
      '&:hover': {
        backgroundColor: 'var(--event-surface-bold-hover)',
      },
      '&[data-editing]': {
        backgroundColor: 'var(--event-surface-selected)',
        '&:hover': {
          backgroundColor: 'var(--event-surface-selected-hover)',
        },
      },
      [`& .${eventCalendarClasses.dayGridEventRecurringIcon}`]: {
        color: 'var(--event-on-surface-bold)',
      },
      '&[data-starting-before-edge]': {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        clipPath: LEFT_ARROW_CLIP,
        paddingLeft: ARROW_DEPTH + 8,
      },
      '&[data-ending-after-edge]': {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        clipPath: RIGHT_ARROW_CLIP,
        paddingRight: ARROW_DEPTH + 8,
      },
      '&[data-starting-before-edge][data-ending-after-edge]': {
        clipPath: BOTH_ARROWS_CLIP,
      },
      ...getArrowFocusVisibleStyles((theme.shape.borderRadius as number) * 0.75),
    },
    '&[data-variant="invisible"]': {
      width: '100%',
      visibility: 'hidden',
      height: 18,
    },
    '&[data-variant="compact"]': {
      height: 'fit-content',

      '&:active': {},
      '&:hover': {
        backgroundColor: (theme.vars || theme).palette.action.hover,
      },
      '&[data-editing]': {
        backgroundColor: 'var(--event-surface-subtle)',
        '&:hover': {
          backgroundColor: 'var(--event-surface-subtle-hover)',
        },
      },
    },
  }),
);

const DayGridEventPlaceholder = styled(CalendarGrid.DayEventPlaceholder, {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventPlaceholder',
})<{ palette?: PaletteName }>(({ theme }) => ({
  ...(DayGridEventBaseStyles(theme) as any),
  minHeight: 18,
  zIndex: 2,
  backgroundColor: 'var(--event-surface-subtle-hover)',
  color: 'var(--event-on-surface-subtle-primary)',
  border: `1px dashed var(--event-on-surface-subtle-secondary)`,
}));

const DayGridEventTitle = styled('p', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventTitle',
})(({ theme }) => ({
  margin: 0,
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: 'var(--EventCalendar-fontSize-eventTitle, 0.75rem)',
  lineHeight: 1.43,
  flexGrow: 1,
  '[data-variant="filled"] &': {
    color: 'var(--event-on-surface-bold)',
  },
  '[data-variant="compact"] &': {
    color: (theme.vars || theme).palette.text.primary,
  },
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)',
  },
  '[data-variant="compact"][data-editing] &': {
    color: 'var(--event-on-surface-subtle-primary)',
  },
}));

const DayGridEventTime = styled('time', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventTime',
})(({ theme }) => ({
  display: 'inline-block',
  color: 'var(--event-on-surface-subtle-secondary)',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.43,
  whiteSpace: 'nowrap',
  paddingInlineEnd: theme.spacing(0.5),
  '@container (width < 300px)': {
    paddingInlineEnd: 0,
    display: 'inline',
    '& > span:last-of-type': {
      display: 'none',
    },
  },
  '[data-variant="compact"] &': {
    color: (theme.vars || theme).palette.text.secondary,
  },
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)',
  },
  '[data-variant="compact"][data-editing] &': {
    color: 'var(--event-on-surface-subtle-secondary)',
  },
}));

const DayGridEventRecurringIcon = styled(RepeatRounded, {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventRecurringIcon',
})(({ theme }) => ({
  color: (theme.vars || theme).palette.text.primary,
  fontSize: '1rem',
  justifySelf: 'flex-end',
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)',
  },
  '[data-variant="compact"][data-editing] &': {
    color: 'var(--event-on-surface-bold)',
  },
}));

const DayGridEventResizeHandler = styled(CalendarGrid.DayEventResizeHandler, {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventResizeHandler',
})({
  position: 'absolute',
  width: 4,
  top: 0,
  bottom: 0,
  zIndex: 3,
  cursor: 'ew-resize',
  opacity: 0,
  '*:hover > &': {
    opacity: 1,
  },
  '&[data-start]': {
    left: 0,
  },
  '&[data-end]': {
    right: 0,
  },
});

const DayGridEventCardWrapper = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventCardWrapper',
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexGrow: 1,
  '@container (width < 300px)': {
    gap: theme.spacing(0.5),
    [`& .${eventCalendarClasses.dayGridEventTitle}`]: {
      marginInlineStart: theme.spacing(0.5),
      paddingInlineEnd: theme.spacing(1.5),
    },
  },
}));

const DayGridEventCardContent = styled('p', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventCardContent',
})({
  margin: 0,
  height: 20,
  lineHeight: '20px',
});

const EventColorIndicator = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'EventColorIndicator',
})({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: 'var(--event-main)',
  marginTop: 2,
});

const DayGridEventLinesClamp = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventLinesClamp',
})({
  display: '-webkit-box',
  WebkitLineClamp: 'var(--number-of-lines)',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  flexGrow: 1,
});

const isResizableSelector = (
  state: EventCalendarState,
  side: SchedulerEventSide,
  occurrence: SchedulerRenderableEventOccurrence,
) => {
  if (!schedulerEventSelectors.isResizable(state, occurrence.id, side)) {
    return false;
  }

  const view = eventCalendarViewSelectors.view(state);

  // There is only one day cell in the day view
  if (view === 'day') {
    return false;
  }

  // In month view, only multi-day and all-day events can be resized
  if (view === 'month') {
    return isOccurrenceAllDayOrMultipleDay(occurrence, state.adapter);
  }

  return true;
};

export const DayGridEvent = React.forwardRef(function DayGridEvent(
  props: DayGridEventProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
) {
  const { occurrence, variant, style: styleProp, className, ...other } = props;

  // Context hooks
  const { classes, localeText } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();
  // Selector hooks
  const isDraggable = useStore(store, schedulerEventSelectors.isDraggable, occurrence.id);
  const isStartResizable = useStore(store, isResizableSelector, 'start', occurrence);
  const isEndResizable = useStore(store, isResizableSelector, 'end', occurrence);
  const isRecurring = useStore(store, schedulerEventSelectors.isRecurring, occurrence.id);

  const color = useStore(store, schedulerEventSelectors.color, occurrence.id, undefined);

  // Feature hooks
  const formatTime = useFormatTime();
  const accessibleName = useEventAccessibleName({
    occurrence,
    isRecurring,
    localeText,
  });

  const content = React.useMemo(() => {
    switch (variant) {
      case 'invisible': {
        return null;
      }
      case 'filled':
      case 'placeholder':
        return (
          <React.Fragment>
            <DayGridEventLinesClamp
              className={classes.dayGridEventLinesClamp}
              style={{ '--number-of-lines': 1 } as React.CSSProperties}
            >
              <DayGridEventTitle className={classes.dayGridEventTitle}>
                {occurrence.title}
              </DayGridEventTitle>
            </DayGridEventLinesClamp>
            {isRecurring && (
              <DayGridEventRecurringIcon
                className={classes.dayGridEventRecurringIcon}
                aria-hidden="true"
                fontSize="small"
              />
            )}
          </React.Fragment>
        );

      case 'compact':
        return (
          <DayGridEventCardWrapper className={classes.dayGridEventCardWrapper}>
            <EventColorIndicator className={classes.eventColorIndicator} aria-hidden="true" />

            <DayGridEventCardContent className={classes.dayGridEventCardContent}>
              <DayGridEventLinesClamp
                className={classes.dayGridEventLinesClamp}
                style={{ '--number-of-lines': 1 } as React.CSSProperties}
              >
                <DayGridEventTime className={classes.dayGridEventTime}>
                  <span>{formatTime(occurrence.displayTimezone.start.value)}</span>
                  <span> - {formatTime(occurrence.displayTimezone.end.value)}</span>
                </DayGridEventTime>
                <DayGridEventTitle className={classes.dayGridEventTitle} as="span">
                  {occurrence.title}
                </DayGridEventTitle>
              </DayGridEventLinesClamp>
            </DayGridEventCardContent>

            {isRecurring && (
              <DayGridEventRecurringIcon
                className={classes.dayGridEventRecurringIcon}
                aria-hidden="true"
                fontSize="small"
              />
            )}
          </DayGridEventCardWrapper>
        );
      default:
        throw new Error(
          'MUI X Scheduler: Unsupported variant provided to DayGridEvent component. ' +
            'The DayGridEvent component only supports specific variant values. ' +
            'Check the component documentation for supported variants.',
        );
    }
  }, [
    variant,
    occurrence.title,
    occurrence.displayTimezone.start.value,
    occurrence.displayTimezone.end.value,
    isRecurring,
    formatTime,
    classes,
  ]);

  const sharedProps = {
    start: occurrence.displayTimezone.start,
    end: occurrence.displayTimezone.end,
    ref: forwardedRef,
    'data-variant': variant,
    'data-palette': color,
    style: {
      '--grid-row': occurrence.position.index,
      '--grid-column-span': occurrence.position.daySpan,
      ...styleProp,
    } as React.CSSProperties,
    ...other,
    className: clsx(className, occurrence.className),
  };

  if (variant === 'placeholder') {
    return (
      <DayGridEventPlaceholder
        aria-hidden={true}
        {...sharedProps}
        className={clsx(classes.dayGridEventPlaceholder, sharedProps.className)}
      >
        {content}
      </DayGridEventPlaceholder>
    );
  }

  return (
    <DayGridEventRoot
      eventId={occurrence.id}
      occurrenceKey={occurrence.key}
      dataTimezone={getOccurrenceDataTimezone(occurrence)}
      isDraggable={isDraggable}
      renderDragPreview={(parameters) => <EventDragPreview {...parameters} />}
      aria-hidden={variant === 'invisible'}
      aria-label={accessibleName}
      {...sharedProps}
      className={clsx(classes.dayGridEvent, sharedProps.className)}
    >
      {isStartResizable && (
        <DayGridEventResizeHandler className={classes.dayGridEventResizeHandler} side="start" />
      )}
      {content}
      {isEndResizable && (
        <DayGridEventResizeHandler className={classes.dayGridEventResizeHandler} side="end" />
      )}
    </DayGridEventRoot>
  );
});
